// ── Realtime singleton ─────────────────────────────────────────────
//
// Wraps socket.io-client into a singleton with a stable API so the
// rest of the app doesn't have to care about reconnects, namespaces,
// or auth-token plumbing.
//
// Behavior:
//   • Lazy connect: nothing happens until connect() is called (called
//     from useWhatsAppRealtime once we know the user is logged in).
//   • Reconnect: socket.io's built-in exponential backoff, capped at 30s.
//   • Heartbeat: socket.io handles its own pings (engine.io 25s); we
//     don't add an additional ping layer — would double-count.
//   • Status: derives ('connecting' | 'connected' | 'reconnecting' |
//     'offline') from socket.io's connect / reconnect_attempt /
//     disconnect events.
//   • subscribe/unsubscribe: thin wrappers around socket.on / off.
//   • Status subscription: separate subscribe-to-status API for UI
//     badges to render the current connection state.
//
// JWT lifecycle: pass the current token to connect(). If the token
// rotates (refresh-token flow), call disconnect() then connect(newToken).

import { io, Socket } from 'socket.io-client';

// ── Types ──────────────────────────────────────────────────────────

export type RealtimeStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'offline';

export type RealtimeEvent =
  | 'wa:realtime:ready'
  | 'wa:realtime:error'
  | 'wa:message:new'
  | 'wa:message:status'
  | 'wa:template:status'
  | 'wa:conversation:assigned'
  | 'wa:conversation:unassigned'
  | 'wa:conversation:status'
  | 'wa:conversation:lead_linked'
  | 'wa:conversation:lead_unlinked'
  | 'wa:conversation:read'
  | 'wa:note:added'
  | 'wa:account:connected'
  | 'wa:account:disconnected'
  // Wildcard — subscribe(*) receives every wa:* event
  | '*';

/** Standard envelope wrapped by the backend RealtimeGateway. */
export interface RealtimeEnvelope<T = unknown> {
  type: string;
  organizationId: string;
  timestamp: string;
  data: T;
}

type Handler = (payload: RealtimeEnvelope) => void;
type StatusHandler = (status: RealtimeStatus) => void;

// ── Module state (singleton) ───────────────────────────────────────

let socket: Socket | null = null;
let currentStatus: RealtimeStatus = 'offline';
const statusHandlers = new Set<StatusHandler>();
// Star handlers receive every wa:* event. Tracked separately so we
// don't re-subscribe per event name on socket.
const starHandlers = new Set<Handler>();

// ── URL helpers ────────────────────────────────────────────────────

/**
 * Resolve the socket.io target URL. We strip the `/api` suffix off
 * VITE_API_URL because socket.io's namespace lives at the server root.
 */
function resolveBaseUrl(): string {
  const apiUrl =
    (import.meta.env.VITE_API_URL as string | undefined) ||
    (import.meta.env.DEV ? 'http://localhost:3000' : window.location.origin);
  // Strip trailing /api if the operator pasted the full API URL.
  return apiUrl.replace(/\/api\/?$/, '');
}

// ── Status broadcasting ────────────────────────────────────────────

function setStatus(next: RealtimeStatus): void {
  if (currentStatus === next) return;
  currentStatus = next;
  for (const h of statusHandlers) {
    try {
      h(next);
    } catch {
      /* never let a bad listener break the loop */
    }
  }
}

// ── Public API ─────────────────────────────────────────────────────

export const realtime = {
  /**
   * Connect the singleton. Safe to call multiple times — second+
   * calls are a no-op as long as the token is the same. If the token
   * changes (refresh-token flow), call disconnect() first.
   */
  connect(token: string): void {
    if (socket && socket.connected) return;
    if (socket && (socket as any).auth?.token === token) {
      // Already configured with this token; let socket.io retry on its own.
      return;
    }
    if (socket) {
      // Token rotated. Tear down the old socket first.
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }

    setStatus('connecting');

    const url = resolveBaseUrl();
    socket = io(`${url}/realtime`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      // Built-in exponential backoff. min 1s, max 30s, ~1.5x growth.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 30_000,
      // socket.io's engine.io handles its own keepalive ping/pong.
      // The 25s timing comes from the server's default pingInterval.
      timeout: 10_000,
    });

    socket.on('connect', () => setStatus('connected'));
    socket.on('disconnect', (reason) => {
      // 'io server disconnect' = backend kicked us (e.g. auth fail).
      // Don't auto-reconnect — caller must reconnect with a fresh token.
      if (reason === 'io server disconnect') {
        setStatus('offline');
        return;
      }
      setStatus('reconnecting');
    });
    socket.on('reconnect', () => setStatus('connected'));
    socket.on('reconnect_attempt', () => setStatus('reconnecting'));
    socket.on('reconnect_failed', () => setStatus('offline'));
    socket.on('connect_error', () => {
      // Stays in 'connecting' / 'reconnecting' — socket.io retries.
    });

    // Fan out wildcard subscribers using socket.io's onAny.
    socket.onAny((eventName: string, payload: RealtimeEnvelope) => {
      // Skip its own lifecycle events that aren't part of our wa:* namespace.
      if (!eventName.startsWith('wa:')) return;
      for (const h of starHandlers) {
        try {
          h(payload);
        } catch {
          /* swallow */
        }
      }
    });
  },

  /** Tear down the connection. Idempotent. */
  disconnect(): void {
    if (!socket) return;
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    setStatus('offline');
  },

  /**
   * Subscribe to a specific event. Returns an unsubscribe function so
   * React effects can clean up cleanly.
   */
  subscribe(event: Exclude<RealtimeEvent, '*'>, handler: Handler): () => void {
    if (socket) {
      socket.on(event, handler as any);
    }
    return () => {
      if (socket) socket.off(event, handler as any);
    };
  },

  /**
   * Subscribe to ALL wa:* events. Returns an unsubscribe function.
   * Mainly for the debug overlay + the central useWhatsAppRealtime
   * hook that needs to know about everything.
   */
  subscribeAll(handler: Handler): () => void {
    starHandlers.add(handler);
    return () => {
      starHandlers.delete(handler);
    };
  },

  /** Current connection status, for one-shot reads. */
  getStatus(): RealtimeStatus {
    return currentStatus;
  },

  /**
   * Subscribe to status changes. The handler is invoked immediately
   * with the current status, then on every transition. Returns an
   * unsubscribe function.
   */
  subscribeStatus(handler: StatusHandler): () => void {
    statusHandlers.add(handler);
    // Fire current value synchronously so UIs paint correctly on mount.
    try {
      handler(currentStatus);
    } catch {
      /* swallow */
    }
    return () => {
      statusHandlers.delete(handler);
    };
  },

  /**
   * Manually request a reconnect attempt. Called by the status badge
   * when the user clicks "Reconnect now". No-op if already connected.
   */
  reconnect(): void {
    if (!socket) return;
    if (socket.connected) return;
    setStatus('reconnecting');
    socket.connect();
  },

  /** Whether the singleton currently holds an active socket. */
  isInitialized(): boolean {
    return socket !== null;
  },
};
